import { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

// react router
import { Link, useNavigate } from 'react-router-dom';

// utilities
import { isEmpty } from 'lodash';

// translations
import { FormattedMessage, injectIntl } from 'react-intl';

// redux
import { _login, _checkPageAccess } from '../../redux/actions';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../redux/slices/userDataSlice';
import { setLoggedIn } from '../../../../redux/slices/loggedIn';
import { setFunds, setWithdrawals, setBtcBalance, setDeposits } from '../../../../redux/slices/fundsSlice';
import { _getFundsData } from '../../../../redux/actions';

// components
import { Form } from 'reactstrap';
import 'react-toastify/dist/ReactToastify.css';
import InputCode from '../../../../components/InputCode';
import Honeypot from '../../../../components/Honeypot';
import WealthTechLogoLoader from '../../../../components/WealthTechLogoLoader/WealthTechLogoLoader';
import { setSnackbarOpen, setSnackbarData } from '../../../snackbar/redux/snackbarSlice';
import { showErrorSnackbar } from '../../../snackbar/helpers';

// holiday notice
import { shouldShowHolidayNotice, showHolidayNoticeModal } from '../../../modals/holiday-notice/helpers';

// ** Icons Imports
import { ReactComponent as OTPIcon } from '../../../../assets/icons/imported/OTP.svg';

// form validations
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// Fallback values for environment variables
const SITE_KEY = process.env.REACT_APP_SITE_KEY || '6LeFxpEpAAAAADwQ0FRqy_ukCnYxtA23xqgxm-oW' // Default fallback, will be replaced by actual env var in production
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY || '10000000-ffff-ffff-ffff-ffffffffffff' // Test key
const IS_PRODUCTION = false // Default fallback for development

// login schema
const logInSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string().required('Password is required')
})

const LoginTab = () => {
  // ** URL Params
  const urlParams = new URLSearchParams(window.location.search)
  const redirectUrl = decodeURIComponent(urlParams.get('redirect_url'))

  // ** States
  const [step, setStep] = useState(1)
  const [otpCode, setOtpCode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [recaptchaConfirmed, setRecaptchaConfirmed] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480)
  const [pendingData, setPendingData] = useState(null)
  const [pageAccessAllowed, setPageAccessAllowed] = useState(false)
  const [pageAccessLoading, setPageAccessLoading] = useState(true)
  const [pageAccessError, setPageAccessError] = useState(null)
  const captchaRef = useRef(null)

  const theme = useSelector(state => state.theme.value)
  const isLoggedIn = useSelector(state => state.userData.value.email)
  const OPEN_PRIVATE_CONNECTION = 'OPEN_PRIVATE_CONNECTION'
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(logInSchema),
    mode: 'onSubmit'
  })
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const onHCaptchaVerify = token => {
    setRecaptchaConfirmed(true)
    setRecaptchaResponse(token)
    if (pendingData) {
      performLogin(pendingData, token)
    }
  }

  const onHCaptchaError = (error) => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
    setIsLoading(false)
  }

  const onHCaptchaExpire = () => {
    setRecaptchaConfirmed(false)
    setRecaptchaResponse("")
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width <= 768)
      setIsSmallMobile(width <= 480)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Check page access for rate limiting
    setPageAccessLoading(true)
    _checkPageAccess(
      'login',
      (data) => {
        setPageAccessAllowed(true)
        setPageAccessLoading(false)
        setPageAccessError(null)
      },
      (error) => {
        setPageAccessLoading(false)
        if (error?.data?.error === 'rate_limit_exceeded') {
          setPageAccessAllowed(false)
          setPageAccessError(error.data.message || 'Too many requests. Please try again later.')
        } else {
          // Fallback for other errors, still block or allow? User said "unless OK".
          // If API fails completely, safe to block or fallback to allow?
          // Given "unless we got message ok", I should strictly block.
          setPageAccessAllowed(false)
          setPageAccessError(error?.data?.message || 'Unable to verify page access.')
        }
      }
    )
    // if (isLoggedIn) {
    //   // Redirect URL on success if any in URL param
    //   if (redirectUrl) {
    //     console.log('HERE', redirectUrl)
    //     navigate(redirectUrl)
    //   } else {
    //     console.log('HERE', redirectUrl)
    //     navigate('/trade')
    //   }
    // }
  }, [isLoggedIn, history])
  const exchangeName = useSelector(state => state.exchangeCustomization.value.name)

  document.title = `${exchangeName}: Login`
  const _loginSuccessCallback = res => {
    // console.log(res)
    if (res?.email) {
      dispatch(setLoggedIn(true))
      dispatch(setUser(res))
      _getFundsData(
        data => {
          dispatch(setWithdrawals(data.total_withdraw))
          dispatch(setDeposits(data.total_deposit))
          dispatch(setFunds(data.total_balance))
          dispatch(setBtcBalance(data.total_balance_btc))
        },
        error => {
          // console.log(error)
        }
      )
      const user = {
        email: res.email,
        csrf_token: res.csrf_token
      }
      sessionStorage.setItem('user', JSON.stringify(user))
      const userJson = sessionStorage.getItem('user')
      if (userJson) {
        // const user = JSON.parse(userJson)
        // // authenticate the user based on the email and CSRF token
        // // ...
        // if (user.remember_me) {
        //   // set a persistent cookie
        //   document.cookie = `_barong_session=${res.session}; expires=Fri, 31 Dec 2999 23:59:59 GMT; path=/; Secure; SameSite=Strict`
        // } else {
        //   // set a session cookie
        //   document.cookie = `_barong_session=${res.session}; path=/; Secure; SameSite=Strict`
        // }
        // remove the user data from session storage
        //sessionStorage.removeItem('user')
      } else {
        // authentication failed, remove the user data from session storage
        //sessionStorage.removeItem('user')
      }

      // Show holiday notice if applicable (with delay to ensure page loads)
      setTimeout(() => {
        if (shouldShowHolidayNotice(true)) { // true = this is a login event
          showHolidayNoticeModal({
            title: 'Holiday Notice',
            message: 'Our offices will close at end of business on Thursday, 18 December 2025, and will reopen on Monday, 5 January 2026. All deposits and withdrawals submitted during this period will be processed on 5 January 2026.'
          })
        }
      }, 1000)

      // Redirect URL on success if any in URL param
      if (redirectUrl !== 'null') {
        navigate(redirectUrl)
      } else {
        navigate('/account/wallet/overview')
      }
    }
  }

  const onSubmit = data => {
    if (data.company_name) {
      return
    }

    if (!pageAccessAllowed) {
      showErrorSnackbar({
        alertMessage: pageAccessError || 'Too many requests. Please try again later.'
      })
      return
    }

    setIsLoading(true)
    if (isEmpty(errors)) {
      setPendingData(data)
      if (captchaRef.current) {
        captchaRef.current.execute()
      } else {
        // Fallback if captcha is not ready
        performLogin(data, "")
      }
    } else {
      setIsLoading(false)
    }
  }

  const performLogin = (data, captchaToken) => {
    _login(
      {
        email: data.email,
        password: data.password,
        captcha_response: captchaToken,
        ...(step === 2 && { otp_code: otpCode })
      },
      data => {
        setIsLoading(false)
        setPendingData(null)
        _loginSuccessCallback(data)
        dispatch({ type: OPEN_PRIVATE_CONNECTION })
      },
      error => {
        setIsLoading(false)
        setPendingData(null)
        // console.log(error)
        if (error?.errors && error.errors[0] && error.errors[0] === 'identity.session.missing_otp') {
          setStep(2)
        } else {
          dispatch(setSnackbarOpen(true))
          dispatch(
            setSnackbarData({
              alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
              severity: 'error',
              //buttonMessage: 'See order',
              callback: () => { },
              //actionType: 'link', // link - close
              //linkTo: '/account/orders/open',
              position: 'topRight'
            })
          )
        }
      }
    )
  }

  if (pageAccessLoading) {
    return (
      <div style={{ width: '100%', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <WealthTechLogoLoader />
      </div>
    )
  }

  if (!pageAccessAllowed && pageAccessError) {
    return (
      <div style={{ width: '100%', minHeight: '300px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Access Unavailable</h2>
        <p style={{ color: 'var(--text-secondary, #94a3b8)' }}>{pageAccessError}</p>
      </div>
    )
  }

  return (
    <div style={{ width: '100%' }}>
      {step === 1 && (
        <>
          {/* Mobile Logo/Branding */}
          {isMobile && (
            <div style={{
              textAlign: 'center',
              marginBottom: isSmallMobile ? '30px' : '40px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isSmallMobile ? '8px' : '12px',
                marginBottom: isSmallMobile ? '20px' : '25px',
              }}>
                <img
                  src="/logo-small-white.svg"
                  alt="Logo"
                  width={isSmallMobile ? "28" : "35"}
                  height={isSmallMobile ? "28" : "35"}
                  style={{ objectFit: 'contain' }}
                />
                <span style={{
                  fontSize: isSmallMobile ? '24px' : '28px',
                  fontWeight: 700,
                  color: 'var(--text-color, white)',
                }}>WealthTech</span>
              </div>
              <p style={{
                color: 'var(--text-secondary, #94a3b8)',
                fontSize: isSmallMobile ? '14px' : '16px',
                lineHeight: 1.5,
                margin: 0,
              }}>
                Welcome back! Sign in to access your crypto trading platform.
              </p>
            </div>
          )}

          <div style={{ marginBottom: isMobile ? '30px' : '40px' }}>
            <h2 style={{
              fontSize: isMobile ? '28px' : '32px',
              color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
              marginBottom: '10px',
              textAlign: 'left',
              fontWeight: 600,
            }}>Sign In</h2>
            <p style={{
              color: 'var(--text-secondary, #94a3b8)',
              fontSize: isMobile ? '14px' : '16px',
              textAlign: 'left',
              margin: 0,
            }}>Enter your credentials to access your account</p>

            {pageAccessError && (
              <div style={{
                padding: '10px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#ef4444',
                marginTop: '10px',
                fontSize: '14px'
              }}>
                {pageAccessError}
              </div>
            )}
          </div>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Honeypot register={register} />
            <div style={{ marginBottom: isMobile ? '20px' : '25px' }}>
              <label htmlFor="email" style={{
                display: 'block',
                color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
              }}>Email</label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                {...register('email')}
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 16px' : '14px 16px',
                  background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                  border: `1px solid ${errors.email ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                  borderRadius: '8px',
                  color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                  fontSize: '15px',
                  boxSizing: 'border-box',
                  outline: 'none',
                  cursor: pageAccessAllowed ? 'text' : 'not-allowed',
                }}
                disabled={!pageAccessAllowed}
                required
              />
              {errors.email && (
                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.email.message}
                </span>
              )}
            </div>

            <div style={{ marginBottom: isMobile ? '20px' : '25px' }}>
              <label htmlFor="password" style={{
                display: 'block',
                color: theme === 'light' ? '#374151' : 'var(--text-secondary, #cbd5e1)',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
              }}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  placeholder="••••••••••••"
                  {...register('password')}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 45px 12px 16px' : '14px 45px 14px 16px',
                    background: theme === 'light' ? '#f8f9fa' : 'var(--input-background, rgba(30, 50, 70, 0.3))',
                    border: `1px solid ${errors.password ? '#ef4444' : (theme === 'light' ? '#d1d5db' : 'var(--input-border, rgba(100, 150, 180, 0.3))')}`,
                    borderRadius: '8px',
                    color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
                    fontSize: '15px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    cursor: pageAccessAllowed ? 'text' : 'not-allowed',
                  }}
                  disabled={!pageAccessAllowed}
                  required
                />
                <span
                  onClick={togglePasswordVisibility}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-secondary, #64748b)',
                    cursor: 'pointer',
                  }}
                >
                  {showPassword ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  )}
                </span>
              </div>
              {errors.password && (
                <span style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {errors.password.message}
                </span>
              )}
            </div>

            <div style={{
              textAlign: 'right',
              marginTop: '-15px',
              marginBottom: isMobile ? '20px' : '25px',
            }}>
              <Link to={'/recover-password'} style={{
                color: 'var(--primary-color, #009999)',
                textDecoration: 'none',
                fontSize: '14px',
              }}>
                <FormattedMessage id={'page.header.signIn.forgotPassword'} />
              </Link>
            </div>


            {/* hCaptcha (Invisible) */}
            <HCaptcha
              id="hcaptcha-login"
              ref={captchaRef}
              size="invisible"
              sitekey={HCAPTCHA_SITE_KEY}
              onVerify={onHCaptchaVerify}
              onError={onHCaptchaError}
              onExpire={onHCaptchaExpire}
              theme={theme}
            />

            <button
              type="submit"
              disabled={isLoading || !pageAccessAllowed}
              style={{
                width: '100%',
                padding: '14px 16px',
                background: (isLoading || !pageAccessAllowed) ? 'var(--button-disabled, #6b7280)' : 'var(--primary-gradient, linear-gradient(135deg, #009999 0%, #007a7a 100%))',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 600,
                cursor: (isLoading || !pageAccessAllowed) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || !pageAccessAllowed) ? 0.7 : 1,
              }}
            >
              {isLoading ? 'Processing...' : <FormattedMessage id={'page.header.signIn'} />}
            </button>
          </Form>

          <div style={{
            textAlign: 'center',
            color: 'var(--text-secondary, #94a3b8)',
            fontSize: isSmallMobile ? '13px' : isMobile ? '14px' : '15px',
            marginTop: isSmallMobile ? '20px' : isMobile ? '25px' : '30px',
          }}>
            <FormattedMessage id={'page.signIn.signupExists'} />{' '}
            <Link to='/signup' style={{
              color: 'var(--primary-color, #009999)',
              textDecoration: 'none',
              fontWeight: 600,
            }}>
              <FormattedMessage id={'page.signIn.signup'} />
            </Link>
          </div>
        </>
      )}

      {step === 2 && (
        <Form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <OTPIcon
              width={80}
              height={80}
              style={{ margin: '20px auto', display: 'block' }}
            />
            <h2 style={{
              fontSize: isMobile ? '24px' : '28px',
              color: theme === 'light' ? '#111827' : 'var(--text-color, white)',
              marginBottom: '10px',
            }}>
              Confirm OTP Code
            </h2>
            <p style={{
              color: 'var(--text-secondary, #94a3b8)',
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: 1.5,
            }}>
              Let's confirm it's really you logging in! Enter the OTP code from your Authenticator app to proceed.
            </p>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <InputCode
              length={6}
              label='OTP Code'
              onComplete={e => {
                setOtpCode(e)
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              padding: isMobile ? '14px' : '16px',
              background: isLoading ? 'var(--button-disabled, #6b7280)' : 'var(--primary-gradient, linear-gradient(135deg, #009999 0%, #007a7a 100%))',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontSize: '16px',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 4px 15px rgba(0, 153, 153, 0.3)',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? 'Verifying...' : <FormattedMessage id={'page.header.signIn'} />}
          </button>
        </Form>
      )}
    </div>
  )
}
export default injectIntl(LoginTab)
